'use client';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Button as MuiButton, Paper, Stack, Typography } from '@mui/material';
import { Button } from '@prismo-io/design-system';
import { Application, translate } from '@prismo-io/router';
import {
  LanguageCode,
  type LanguageCodeT,
  UserAccessType,
  type UserAccessTypeT,
} from '@prismo-io/schemas';
import Link from 'next/link';
import { type FC, useMemo } from 'react';
import { useSendLeadHasClickedHubspot } from './use-send-lead-has-clicked-hubspot';

type Props = {
  access: UserAccessTypeT;
};

const buildToolsUrl = (locale: LanguageCodeT) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.TOOLS,
    path: '/',
    addHostname: true,
    locale,
  });
};

export const TeasingCard: FC<Props> = (props: Props) => {
  const { access } = props;

  const { trigger: sendLeadHasClickedHubspot } = useSendLeadHasClickedHubspot();

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;
  const toolsUrl = buildToolsUrl(locale as LanguageCodeT);
  const videoUrl = 'https://youtu.be/J_2wZBYdYOw';
  const contactUsUrl = 'https://meetings.hubspot.com/deborah-philippe';

  const launchVideo = () => {
    sendLeadHasClickedHubspot();
    window.open(videoUrl, '_blank');
  };

  const contactUs = () => {
    window.open(contactUsUrl, '_blank');
  };

  const teaserData = useMemo(() => {
    switch (access) {
      case UserAccessType.enum.LEAD:
        return {
          desc: t`Accédez à PRISMO Tools, un panel d'outils d'analyse qui simplifient la gestion des talents.`,
          btn: t`Découvrez la vidéo de présentation de PRISMO`,
        };
      case UserAccessType.enum.EXPERT_FREE:
        return {
          desc: t`Invitez ce talent dans PRISMO Tools pour voir son profil complet.`,
          btn: t`Invitez ce talent`,
        };
      case UserAccessType.enum.STARTER:
        return {
          desc: t`Faites évoluer votre abonnement vers une formule Business, Business +, Corporate ou Sur devis pour avoir accès à ces informations.`,
          btn: t`Faire évoluer mon abonnement`,
        };
      default:
        return {
          desc: t`Testez gratuitement nos outils d'analyse premium pour découvrir l'intégralité du rapport.`,
          btn: t`Testez gratuitement les outils premium`,
        };
    }
  }, [access]);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      position="absolute"
      top="0"
      bottom="0"
      left="0"
      right="0"
      zIndex={1}
    >
      <Stack direction="column" justifyContent="center" margin={3}>
        <Paper elevation={3}>
          <Stack
            width="100%"
            maxWidth="450px"
            direction="column"
            gap={4}
            alignItems="center"
            padding={3}
          >
            <Typography textAlign="center" variant="h2">
              <Trans>Premium</Trans>
            </Typography>
            <Typography textAlign="center">{teaserData.desc}</Typography>
            {access !== UserAccessType.Enum.LEAD && (
              <Stack component="footer" direction="row" justifyContent="center">
                <Button
                  LinkComponent={Link}
                  href={toolsUrl}
                  variant="contained"
                  color="gradient"
                  sx={{
                    textAlign: 'center',
                    '& .MuiButton-startIcon': {
                      display: 'none',
                    },
                  }}
                >
                  {teaserData.btn}
                </Button>
              </Stack>
            )}
            {access === UserAccessType.Enum.LEAD && (
              <Stack
                component="footer"
                direction="column"
                alignItems="center"
                gap={2}
              >
                <MuiButton
                  LinkComponent={Link}
                  // href={videoUrl}
                  // target="_blank"
                  onClick={launchVideo}
                  variant="contained"
                  color="gradient"
                  sx={{
                    textAlign: 'center',
                    '& .MuiButton-startIcon': {
                      display: 'none',
                    },
                  }}
                >
                  {teaserData.btn}
                </MuiButton>
                <MuiButton
                  variant="text"
                  color="inherit"
                  onClick={contactUs}
                  sx={{ textAlign: 'center' }}
                >
                  <Trans>
                    Prenez RDV pour tester gratuitement pendant 14 jours
                  </Trans>
                </MuiButton>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};
